import { isArray, isString, hasValue, isObject, isStringArray, isNumber, isAbortSignal, isFunction, isStatusCode } from '@tg-resources/is';
import renderTemplate from 'lodash.template';
import cookie from 'cookie';

class ValidationErrorInterface {
    constructor(errors) {
        // Store errors
        this._errors = errors;
    }
    get errors() {
        return this._errors;
    }
    // Support for .. of loops
    [Symbol.iterator]() {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const instance = this;
        let curKey = 0;
        let done = false;
        return {
            next() {
                const nextVal = instance.errorByIndex(curKey);
                // Note: If a custom error handler does not coerce undefined to null,
                //  the iterator will stop too early
                //
                // Feel free to submit a PR if this annoys you!
                if (nextVal === undefined) {
                    done = true;
                }
                else {
                    curKey += 1;
                }
                return {
                    done,
                    value: nextVal,
                };
            },
        };
    }
    /**
     * Used by firstError and iteration protocol
     */
    errorByIndex(index) {
        return this._errors[index];
    }
    /* istanbul ignore next: just an interface */
    hasError() {
        return this._errors.length > 0;
    }
    bindToField(fieldName) {
        // istanbul ignore next: Only happens w/ custom error handlers
        if (process.env.NODE_ENV !== 'production') {
            if (this.fieldName && this.fieldName !== fieldName) {
                // eslint-disable-next-line no-console
                console.error(`ValidationErrorInterface: Unexpected rebind of ${this} as ${fieldName} (was ${this.fieldName})`);
            }
        }
        this.fieldName = fieldName;
    }
    toString() {
        return this.asString();
    }
    map(callbackfn, thisArg) {
        return this._iter().map(callbackfn, thisArg);
    }
    forEach(callbackfn, thisArg) {
        this._iter().forEach(callbackfn, thisArg);
    }
    filter(callbackfn, thisArg) {
        return this._iter().filter(callbackfn, thisArg);
    }
    /**
     * Iterator used for .forEach/.filter/.map
     */
    _iter() {
        return this._errors;
    }
}
class ResourceErrorInterface {
    constructor(message) {
        this._message = message;
    }
    toString() {
        return this._message;
    }
    get isNetworkError() {
        return false;
    }
    // istanbul ignore next: Tested in packages that implement Resource
    get isInvalidResponseCode() {
        return false;
    }
    get isValidationError() {
        return false;
    }
    get isAbortError() {
        return false;
    }
}
class ResponseInterface {
    // istanbul ignore next: Tested in package that implement Resource
    constructor(response, error = null, request = null) {
        this._response = response;
        this._error = error;
        this._request = request;
    }
    get response() {
        // istanbul ignore next: Tested in package that implement Resource
        return this._response;
    }
    get error() {
        return this._error;
    }
    get hasError() {
        return !!this.error;
    }
    get statusCode() {
        // istanbul ignore next: Tested in package that implement Resource
        return this.status;
    }
}

const bindAndCoerce = (error, fieldName) => {
    const res = error || null;
    if (res !== null) {
        res.bindToField(fieldName);
    }
    return res;
};
class SingleValidationError extends ValidationErrorInterface {
    constructor(errors) {
        // istanbul ignore next: never false in tests
        if (process.env.NODE_ENV !== 'production') {
            if (!isArray(errors) ||
                errors.filter(x => !isString(x)).length > 0) {
                // eslint-disable-next-line no-console
                console.error('SingleValidationError: `errors` argument must be an array of strings');
            }
        }
        super(errors);
    }
    hasError() {
        return this._errors.length > 0;
    }
    asString(glue = ' ') {
        return this._errors.join(glue);
    }
}
class ParentValidationErrorInterface extends ValidationErrorInterface {
    constructor() {
        super(...arguments);
        // If null, there are no nonFieldErrors
        //
        // Some parent validation errors cannot have nonFieldErrors (e.g. ListValidationError,
        //  since a list cannot have attributes in JSON). Helps keep things DRY, since the result
        //  of getError & firstError won't change if nonFieldErrors is always null.
        this.nonFieldErrors = null;
    }
    getError(fieldName, allowNonFields = false) {
        if (isArray(fieldName)) {
            let error = null;
            // Might want to add check to avoid empty errors
            fieldName.forEach((field, idx) => {
                if (!error && idx === 0) {
                    error = this.errors[field];
                }
                else if (error) {
                    error = error.errors[field] || null;
                }
            });
            return error;
        }
        if ((hasValue(fieldName) && this.errors[fieldName]) ||
            (allowNonFields && this.nonFieldErrors)) {
            return ((hasValue(fieldName) ? this.errors[fieldName] : null) ||
                this.nonFieldErrors ||
                null);
        }
        return null;
    }
    firstError(allowNonField = false) {
        if (allowNonField && this.nonFieldErrors) {
            return this.nonFieldErrors;
        }
        return this.errorByIndex(0) || null;
    }
}
class ListValidationError extends ParentValidationErrorInterface {
    constructor(errors, nonFieldErrors = null) {
        // istanbul ignore else: never false in tests
        if (process.env.NODE_ENV !== 'production') {
            // Takes: anything thats not a ValidationError (besides null)
            const filterFn = (x) => {
                if (x === null) {
                    return false;
                }
                return !x || !(x instanceof ValidationErrorInterface);
            };
            if (errors) {
                /* eslint-disable no-console */
                // istanbul ignore next: safeguard
                if (!isArray(errors) || errors.filter(filterFn).length > 0) {
                    console.error('ListValidationError: `errors argument` must be an array of ValidationErrorInterface? instances');
                    console.error('    Supported Builtins: null/SingleValidationError/ListValidationError/ValidationError');
                }
            }
        }
        // MAP: falsy to null, bind all errors w/ their fieldName
        super((errors || []).map(bindAndCoerce));
        this.nonFieldErrors = nonFieldErrors || null;
        if (this.nonFieldErrors !== null) {
            this.nonFieldErrors.bindToField('nonFieldErrors');
        }
    }
    hasError() {
        return (this._errors.filter((x) => x && x.hasError && x.hasError())
            .length > 0);
    }
    asString(glue = '; ') {
        return this._errors
            .map((value, key) => `${key}: ${value ? value.asString() : null}`)
            .join(glue);
    }
}
class ValidationError extends ParentValidationErrorInterface {
    constructor(errors = null, nonFieldErrors = null) {
        // istanbul ignore else: never false in tests
        if (process.env.NODE_ENV !== 'production') {
            // Takes: anything thats not a ValidationError (besides null)
            const filterFn = (key) => {
                if (!errors || errors[key] === null) {
                    return false;
                }
                return (!errors[key] ||
                    !(errors[key] instanceof ValidationErrorInterface));
            };
            if (errors) {
                if (!isObject(errors) ||
                    Object.keys(errors).filter(filterFn).length > 0) {
                    /* eslint-disable no-console */
                    console.error('ListValidationError: `errors argument` must be an object of ValidationErrorInterface? instances');
                    console.error('    Supported Builtins: null/SingleValidationError/ListValidationError/ValidationError');
                    /* eslint-enable no-console */
                }
            }
        }
        // MAP: falsy to null, bind all errors w/ their fieldName
        const mutErrors = {};
        if (errors) {
            Object.keys(errors).forEach(fieldName => {
                mutErrors[fieldName] = bindAndCoerce(errors[fieldName], fieldName);
            });
        }
        super(mutErrors);
        // Bind nonFieldErrors
        this.nonFieldErrors = nonFieldErrors || null;
        if (this.nonFieldErrors !== null) {
            this.nonFieldErrors.bindToField('nonFieldErrors');
        }
        // store a list of keys (for iteration)
        this._errKeys = Object.keys(this._errors);
    }
    hasError() {
        return this.nonFieldErrors !== null || this._errKeys.length > 0;
    }
    asString(glue = '; ') {
        let prefix = '';
        if (this.nonFieldErrors) {
            prefix = `${this.nonFieldErrors}${this._errKeys.length ? glue : ''}`;
        }
        return (prefix +
            this._errKeys
                .map(k => `${k}: ${this._errors[k].asString()}`)
                .join(glue));
    }
    _iter() {
        return Object.keys(this._errors).map(x => this.errors[x]);
    }
    errorByIndex(index) {
        return this.errors[this._errKeys[index]];
    }
}
/**
 * Convert errorText (json or normal text) to a ValidationError
 *
 * @param {*} errorText
 * @param {Object} parentConfig
 */
function parseErrors(errorText, parentConfig) {
    let error;
    if (isString(errorText)) {
        if (errorText) {
            try {
                error = JSON.parse(errorText);
            }
            catch (e) {
                // if json parsing fails, handle as text
                error = errorText;
            }
        }
        else {
            error = '';
        }
    }
    else {
        error = errorText;
    }
    // force undefined to be a string
    if (error === undefined) {
        error = `${undefined}`;
    }
    const result = parentConfig.prepareError(error, parentConfig);
    if (!result) {
        return result;
    }
    // istanbul ignore else: can only happen w/ custom prepareError
    if (result instanceof ValidationErrorInterface) {
        return result.hasError() ? result : null;
    }
    // For anything else, just return the result (why would anyone do this though?)
    // istanbul ignore next: can only happen w/ custom prepareError
    return result;
}
/**
 * Convert an error into a subclass of ValidationErrorInterface
 *
 * @param {*} err
 * @param {Object} parentConfig
 */
function prepareError(err, parentConfig) {
    if (isString(err)) {
        // Note: SingleValidationError contains a list of errors per field
        return new SingleValidationError([err || '#$empty-message$#']);
    }
    if (isArray(err)) {
        // If the array contains only strings, turn it into a SingleValidationError
        if (isStringArray(err)) {
            return new SingleValidationError(err);
        }
        // Parse children of the error and continue
        const errors = err.map((x) => parentConfig.prepareError(x, parentConfig));
        // Should be a nested error, turn it into ListValidationError
        return new ListValidationError(errors);
    }
    if (isObject(err)) {
        let errors = err;
        if ('errors' in errors && hasValue(errors.errors)) {
            errors = errors.errors;
        }
        let resNonField = null;
        const resErrors = {};
        Object.keys(errors).forEach(key => {
            const error = parentConfig.prepareError(errors[key], parentConfig);
            if (key === 'non_field_errors') {
                resNonField = error;
            }
            else if (error !== null) {
                // note: we only add errors as fields if we can parse the field error (or need to)
                resErrors[key] = error;
            }
        });
        // if no errors ensure atleast a default message is set
        if (Object.keys(resErrors).length === 0 && !resNonField) {
            // istanbul ignore else: Should only happen with custom error parsers
            if (Object.keys(err).length === 0) {
                // We are probably inside a ListValidationError where
                // this item is not an error, represented as `{}` by DRF
                return new ValidationError({});
            }
            // istanbul ignore next: Should only happen with custom error parsers
            resNonField = new SingleValidationError([
                hasValue(err) ? JSON.stringify(err) : '#$empty-message$#',
            ]);
        }
        return new ValidationError(resErrors, resNonField);
    }
    // If undefined/null, lets turn it into a null
    if (!hasValue(err)) {
        return null;
    }
    // Everything else gets turned into a string (bools, numbers, binary)
    // Note: Empty string means there is no error
    err = `${err}`;
    // istanbul ignore else: only custom classes w/ custom toString
    if (err) {
        return new SingleValidationError([err]);
    }
    // istanbul ignore next: only custom classes w/ custom toString
    return null;
}

const DEFAULTS = {
    apiRoot: '',
    mutateResponse: null,
    mutateError: null,
    mutateRawResponse: null,
    headers: null,
    cookies: null,
    withCredentials: false,
    parseErrors,
    prepareError,
    statusSuccess: [200, 201, 204],
    statusValidationError: [400],
    defaultAcceptHeader: 'application/json',
    allowAttachments: false,
    signal: null,
};

const isFetchMethod = (method) => ['fetch', 'head', 'options'].includes(method);
const isPostMethod = (method) => ['post', 'patch', 'put', 'del'].includes(method);
function mergeConfig(...config) {
    const res = {};
    config.filter(x => !!x).forEach(opts => Object.assign(res, opts));
    if (res.statusSuccess) {
        if (!isArray(res.statusSuccess) &&
            hasValue(res.statusSuccess) &&
            isNumber(res.statusSuccess)) {
            res.statusSuccess = [res.statusSuccess];
        }
    }
    if (res.statusValidationError) {
        if (!isArray(res.statusValidationError) &&
            hasValue(res.statusValidationError) &&
            isNumber(res.statusValidationError)) {
            res.statusValidationError = [res.statusValidationError];
        }
    }
    if (res.signal && !isAbortSignal(res.signal)) {
        throw new Error(`Expected signal to be an instanceof AbortSignal`);
    }
    // Expect to be filled by now - we use default config which will fill all the right data
    return res;
}
function truncate(value, limit) {
    if (!value || value.length < limit) {
        return value;
    }
    /* istanbul ignore else: safeguard */
    if (!isString(value)) {
        value = `${value}`;
    }
    return `${value.substring(0, limit - 3)}...`;
}
function serializeCookies(cookieVal) {
    /* istanbul ignore else: safeguard */
    if (isObject(cookieVal)) {
        // istanbul ignore next: Tested in package that implement Resource
        return Object.keys(cookieVal)
            .filter((key) => hasValue(cookieVal[key]))
            .map((key) => cookie.serialize(key, cookieVal[key]))
            .join('; ');
    }
    /* istanbul ignore next: safeguard */
    return null;
}

class AbortError extends ResourceErrorInterface {
    constructor(error) {
        super('AbortError: The user aborted a request.');
        this.error = error;
        this.name = 'AbortError';
        this.type = (error ? error.type : null) || 'aborted';
    }
    get isAbortError() {
        return true;
    }
}
class NetworkError extends ResourceErrorInterface {
    constructor(error) {
        super('NetworkError');
        this.error = error;
    }
    get isNetworkError() {
        return true;
    }
}
class InvalidResponseCode extends ResourceErrorInterface {
    constructor(statusCode = null, responseText, type = 'InvalidResponseCode') {
        super(`${type} ${statusCode}: ${truncate(responseText, 256)}`);
        this.statusCode = statusCode;
        this.responseText = responseText;
    }
    get isInvalidResponseCode() {
        return true;
    }
}
class RequestValidationError extends InvalidResponseCode {
    constructor(statusCode = null, responseText, config = null) {
        super(statusCode, responseText, 'RequestValidationError');
        // Set custom config
        this._customConfig = Object.assign({ parseErrors,
            prepareError }, (config || {}));
        // parse response body as a ValidationError
        // _customConfig has parseErrors so we just type cast to ensure this condition is fulfilled
        this._errors = this._customConfig.parseErrors(responseText, this._customConfig);
    }
    // public api
    get isValidationError() {
        return true;
    }
    get isInvalidResponseCode() {
        return false;
    }
    get errors() {
        return this._errors;
    }
    hasError() {
        if (hasValue(this._errors)) {
            return this._errors.hasError();
        }
        // istanbul ignore next: Only happens on network errors
        return false;
    }
}

class Route {
    constructor(config = null) {
        this._customConfig = null;
        this._routeName = '';
        this._parent = null;
        this._config = null;
        this._customConfig = config;
        if (config && 'signal' in config) {
            throw new Error('AbortSignal is not supported at top-level.');
        }
    }
    get parent() {
        return this._parent;
    }
    /**
     * Internal API. Not for public usage.
     * @param parent Parent Router instance
     * @param routeName Bound name
     * @private
     */
    setParent(parent, routeName) {
        if (!this.isBound) {
            this._parent = parent;
            this._routeName = routeName;
        }
    }
    /**
     * Returns true if this route bound to Router instance.
     */
    get isBound() {
        return !!this._parent || !!this._config;
    }
    /**
     * Get current route name in format: `parent.routeName`.
     *  Default is empty string.
     */
    get routeName() {
        if (!this.parent) {
            return this._routeName;
        }
        const parentName = this.parent.routeName;
        if (!parentName) {
            return this._routeName;
        }
        return `${this.parent.routeName}.${this._routeName}`;
    }
    /**
     * Internal API.
     * @private
     */
    getConfig() {
        return this._config;
    }
    setConfig(config) {
        // Update _customConfig
        this._customConfig = Object.assign(Object.assign({}, this._customConfig), (config || {}));
        // Reset _config so it is recreated in the next call to .config
        this.clearConfigCache();
    }
}

class Resource extends Route {
    /**
     * @param apiEndpoint Endpoint used for this resource. Supports ES6 token syntax, e.g: "/foo/bar/${pk}"
     * @param config Customize config for this resource (see `Router.config`)
     */
    constructor(apiEndpoint, config = null) {
        super(config);
        this.fetch = (kwargs, query, requestConfig) => {
            return this._fetch(kwargs, query, requestConfig, 'get');
        };
        this.head = (kwargs, query, requestConfig) => {
            // istanbul ignore next: Tested in package that implement Resource
            return this._fetch(kwargs, query, requestConfig, 'head');
        };
        this.options = (kwargs, query, requestConfig) => {
            // istanbul ignore next: Tested in package that implement Resource
            return this._fetch(kwargs, query, requestConfig, 'options');
        };
        this.post = (kwargs, data, query, attachments, requestConfig) => {
            return this._post(kwargs, data, query, attachments, requestConfig, 'post');
        };
        this.patch = (kwargs, data, query, attachments, requestConfig) => {
            return this._post(kwargs, data, query, attachments, requestConfig, 'patch');
        };
        this.put = (kwargs, data, query, attachments, requestConfig) => {
            return this._post(kwargs, data, query, attachments, requestConfig, 'put');
        };
        this.del = (kwargs, data, query, attachments, requestConfig) => {
            return this._post(kwargs, data, query, attachments, requestConfig, 'del');
        };
        this._apiEndpoint = apiEndpoint;
    }
    get apiEndpoint() {
        return this._apiEndpoint;
    }
    config(requestConfig = null) {
        if (!this._config) {
            this._config = mergeConfig(this.parent ? this.parent.config() : DEFAULTS, this._customConfig);
        }
        if (requestConfig && isObject(requestConfig)) {
            return mergeConfig(this._config, requestConfig);
        }
        return this._config;
    }
    clearConfigCache() {
        this._config = null;
    }
    getHeaders(requestConfig = null) {
        const config = this.config(requestConfig);
        const headers = Object.assign(Object.assign({}, (this.parent ? this.parent.getHeaders() : {})), ((isFunction(config.headers)
            ? config.headers()
            : config.headers) || {}));
        const cookieVal = serializeCookies(this.getCookies(requestConfig));
        if (cookieVal) {
            headers.Cookie = cookieVal;
        }
        // if Accept is null/undefined, add default accept header automatically (backwards incompatible for text/html)
        if (!hasValue(headers.Accept)) {
            headers.Accept = config.defaultAcceptHeader;
        }
        return headers;
    }
    getCookies(requestConfig = null) {
        const config = this.config(requestConfig);
        return Object.assign(Object.assign({}, (this.parent ? this.parent.getCookies() : {})), ((isFunction(config.cookies)
            ? config.cookies()
            : config.cookies) || {}));
    }
    renderPath(urlParams = null, requestConfig = null) {
        let thePath = this.apiEndpoint;
        const config = this.config(requestConfig);
        // istanbul ignore next: Tested in package that implement Resource
        if (isObject(urlParams)) {
            thePath = renderTemplate(this.apiEndpoint)(urlParams);
        }
        return `${config.apiRoot}${thePath}`;
    }
    _fetch(kwargs = null, query = null, requestConfig = null, method) {
        const thePath = this.renderPath(kwargs, requestConfig);
        return this.handleRequest(this.createRequest(method, thePath, query, null, null, requestConfig), requestConfig);
    }
    _post(kwargs = null, data = null, query = null, attachments = null, requestConfig = null, method) {
        const config = this.config(requestConfig);
        // istanbul ignore next: Tested in package that implement Resource
        if (attachments && !config.allowAttachments) {
            throw new Error('Misconfiguration: "allowAttachments=true" is required when sending attachments!');
        }
        const thePath = this.renderPath(kwargs, requestConfig);
        return this.handleRequest(this.createRequest(method, thePath, query, data || {}, attachments, requestConfig), requestConfig);
    }
    mutateRawResponse(rawResponse, requestConfig) {
        const config = this.config(requestConfig);
        // istanbul ignore next: Tested in package that implement Resource
        if (isFunction(config.mutateRawResponse)) {
            return config.mutateRawResponse(rawResponse, requestConfig);
        }
        return rawResponse;
    }
    mutateResponse(responseData, rawResponse, requestConfig) {
        const config = this.config(requestConfig);
        // istanbul ignore next: Tested in package that implement Resource
        if (isFunction(config.mutateResponse)) {
            return config.mutateResponse(responseData, rawResponse, this, requestConfig);
        }
        return responseData;
    }
    mutateError(error, rawResponse, requestConfig) {
        // istanbul ignore next: Tested in package that implement Resource
        const config = this.config(requestConfig);
        // istanbul ignore next: Tested in package that implement Resource
        if (isFunction(config.mutateError)) {
            return config.mutateError(error, rawResponse, this, config);
        }
        // istanbul ignore next: Tested in package that implement Resource
        return error;
    }
    handleRequest(req, requestConfig) {
        return this.ensureStatusAndJson(new Promise(resolve => {
            const headers = this.getHeaders(requestConfig);
            if (headers && isObject(headers)) {
                Object.keys(headers).forEach(key => {
                    if (hasValue(headers[key])) {
                        req = this.setHeader(req, key, headers[key]);
                    }
                });
            }
            this.doRequest(req, (response, error) => resolve(this.wrapResponse(response, error, req)));
        }), requestConfig);
    }
    ensureStatusAndJson(prom, requestConfig) {
        const config = this.config(requestConfig);
        return prom.then((origRes) => {
            const res = this.mutateRawResponse(origRes, requestConfig);
            // If no error occured, e.g we have response and !hasError
            // istanbul ignore next: Tested in package that implement Resource
            if (res && !res.hasError) {
                if (isStatusCode(config.statusSuccess, res.status)) {
                    // Got statusSuccess response code, lets resolve this promise
                    return this.mutateResponse(res.data, res, requestConfig);
                }
                if (isStatusCode(config.statusValidationError, res.status)) {
                    // Got statusValidationError response code, lets throw RequestValidationError
                    throw this.mutateError(new RequestValidationError(res.status, res.text, config), res, requestConfig);
                }
                else {
                    // Throw a InvalidResponseCode error
                    throw this.mutateError(new InvalidResponseCode(res.status, res.text), res, requestConfig);
                }
            }
            else {
                let error;
                if (res && res.wasAborted) {
                    error = new AbortError(res.error);
                }
                else {
                    // res.hasError should only be true if network level errors occur (not statuscode errors)
                    const message = res && res.hasError ? res.error : '';
                    error = new NetworkError(message ||
                        'Something went awfully wrong with the request, check network log.');
                }
                throw this.mutateError(error, res, requestConfig);
            }
        });
    }
}

function bindResources(routes, $this) {
    const res = {};
    Object.keys(routes).forEach(routeName => {
        if (!routes[routeName] ||
            !(routes[routeName] instanceof Router || // eslint-disable-line @typescript-eslint/no-use-before-define
                routes[routeName] instanceof Resource)) {
            throw new Error(`All routes must be instances of Router or Resource (see '${routeName}')`);
        }
        if (routeName[0] === '_') {
            throw new Error(`Route '${routeName}' is invalid. Route names must not start with an underscore`);
        }
        if (routeName === 'config') {
            throw new Error(`Route ${routeName} collides with Router built-in method names`);
        }
        if (routes[routeName].isBound) {
            throw new Error(`Route '${routeName}' is bound already`);
        }
        // add to res
        res[routeName] = routes[routeName];
        // link them up
        res[routeName].setParent($this, routeName);
    });
    const childKeys = $this._childKeys.concat(Object.keys(res));
    try {
        Object.assign($this, res, { _childKeys: childKeys });
    }
    catch (e) {
        if (e instanceof TypeError) {
            let fieldName = /property ([^\s]+) of/gi.exec(`${e}`);
            if (fieldName) {
                fieldName = `Route ${fieldName[1]} collides`;
            }
            else {
                /* istanbul ignore next: only happens w/ weird JS implementation */
                fieldName = 'Some route collides';
            }
            throw new Error(`${fieldName} with Router built-in method names`);
        }
        else {
            /* istanbul ignore next: only happens Object.assign is not available */
            throw e;
        }
    }
}
class Router extends Route {
    constructor(routes = null, config = null) {
        super(config);
        this._childKeys = [];
        const defaultRoutes = this.constructor.defaultRoutes;
        this._childKeys = [];
        if (defaultRoutes) {
            bindResources(defaultRoutes, this);
        }
        // Set routes
        if (routes) {
            bindResources(routes, this);
        }
    }
    getHeaders() {
        const config = this.config();
        return Object.assign(Object.assign({}, (this.parent ? this.parent.getHeaders() : {})), ((isFunction(config.headers)
            ? config.headers()
            : config.headers) || {}));
    }
    getCookies() {
        const config = this.config();
        return Object.assign(Object.assign({}, (this.parent ? this.parent.getCookies() : {})), ((isFunction(config.cookies)
            ? config.cookies()
            : config.cookies) || {}));
    }
    config() {
        if (!this._config) {
            this._config = mergeConfig(this._parent ? this._parent.config() : DEFAULTS, this.constructor.defaultConfig || null, this._customConfig);
        }
        return this._config;
    }
    clearConfigCache() {
        this._config = null;
        this._childKeys.forEach(key => {
            this[key].clearConfigCache();
        });
    }
}
Router.defaultRoutes = null;
Router.defaultConfig = null;

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

const isResourceTuple = (value) => isArray(value) &&
    value.length === 2 &&
    typeof value[0] === 'string' &&
    isObject(value[1]);
const isResourceConstructorObject = (value) => isObject(value) && 'apiEndpoint' in value;
const createResource = (resourceKlass, apiEndpoint, config, _0) => new resourceKlass(apiEndpoint, config);
function createRouter(routes, config, resourceKlass, createResourceFactory = createResource) {
    const routeMap = {};
    Object.keys(routes).forEach(key => {
        if (routes[key] instanceof Router) {
            routeMap[key] = routes[key];
        }
        else if (isString(routes[key])) {
            routeMap[key] = createResourceFactory(resourceKlass, routes[key], null);
        }
        else if (isResourceTuple(routes[key])) {
            const [apiEndpoint, resourceConfig] = routes[key];
            routeMap[key] = createResourceFactory(resourceKlass, apiEndpoint, resourceConfig);
        }
        else if (isResourceConstructorObject(routes[key])) {
            const _a = routes[key], { apiEndpoint } = _a, resourceConfig = __rest(_a, ["apiEndpoint"]);
            routeMap[key] = createResourceFactory(resourceKlass, apiEndpoint, resourceConfig);
        }
        else if (isObject(routes[key])) {
            routeMap[key] = createRouter(routes[key], null, resourceKlass, createResourceFactory);
        }
        else {
            const types = [
                'string',
                '[string, config]',
                '{apiEndpoint: string, ..config}',
                'Router',
            ].join(',');
            throw new Error(`Unknown type used "${key}", one of [${types}] is allowed`);
        }
    });
    return new Router(routeMap, config);
}

export { AbortError, InvalidResponseCode, ListValidationError, NetworkError, ParentValidationErrorInterface, RequestValidationError, Resource, ResourceErrorInterface, ResponseInterface, Router, SingleValidationError, ValidationError, ValidationErrorInterface, bindResources, createResource, createRouter, isFetchMethod, isPostMethod, isResourceConstructorObject, isResourceTuple, mergeConfig, parseErrors, prepareError, serializeCookies, truncate };
